interface VersionInfo {
    totalCw: number;
    ecPerBlock: number;
    b1: number;
    d1: number;
    b2: number;
    d2: number;
    align: number[];
}

/**
 * Self-contained QR Code generator in TypeScript (ISO/IEC 18004 compliant).
 * Generates an SVG path for otpauth:// URIs and standard text.
 */
class QrCodeServiceClass {
    // GF(256) Tables
    private readonly ExpTable: number[] = new Array<number>(512).fill(0);
    private readonly LogTable: number[] = new Array<number>(256).fill(0);
    private IsGfInitialized = false;

    private initGf(): void {
        if (this.IsGfInitialized) return;
        let val = 1;
        for (let i = 0; i < 255; i++) {
            this.ExpTable[i] = val;
            this.LogTable[val] = i;
            val <<= 1;
            if (val >= 256) {
                val ^= 0x11d; // x^8 + x^4 + x^3 + x^2 + 1
            }
        }
        for (let i = 255; i < 512; i++) {
            this.ExpTable[i] = this.ExpTable[i - 255];
        }
        this.IsGfInitialized = true;
    }

    private gfMul(x: number, y: number): number {
        if (x === 0 || y === 0) return 0;
        return this.ExpTable[this.LogTable[x] + this.LogTable[y]];
    }

    private getVersionInfo(version: number): VersionInfo {
        const table: [number, number, number, number, number, number, number[]][] = [
            [0, 0, 0, 0, 0, 0, []],
            [26, 10, 1, 16, 0, 0, []],
            [44, 16, 1, 28, 0, 0, [6, 18]],
            [70, 26, 1, 44, 0, 0, [6, 22]],
            [100, 18, 2, 32, 0, 0, [6, 26]],
            [134, 24, 2, 43, 0, 0, [6, 30]],
            [172, 16, 4, 27, 0, 0, [6, 34]],
            [196, 18, 4, 31, 0, 0, [6, 22, 38]],
            [242, 22, 2, 38, 2, 39, [6, 24, 42]],
            [292, 22, 3, 36, 2, 37, [6, 26, 46]],
            [346, 26, 4, 43, 1, 44, [6, 28, 50]],
        ];
        const row = table[version] ?? table[1];
        return {
            totalCw: row[0],
            ecPerBlock: row[1],
            b1: row[2],
            d1: row[3],
            b2: row[4],
            d2: row[5],
            align: row[6],
        };
    }

    private determineVersion(dataLength: number): number {
        for (let v = 1; v <= 10; v++) {
            const info = this.getVersionInfo(v);
            const capacity = info.b1 * info.d1 + info.b2 * info.d2;
            const headerBits = v <= 9 ? 12 : 20;
            const maxDataLen = Math.floor((capacity * 8 - headerBits) / 8);
            if (dataLength <= maxDataLen) {
                return v;
            }
        }
        throw new Error('Data too long for QR code.');
    }

    private encodeData(dataBytes: number[], version: number): number[] {
        const info = this.getVersionInfo(version);
        const totalDataCw = info.b1 * info.d1 + info.b2 * info.d2;
        const totalBitsReq = totalDataCw * 8;

        let bits = '0100'; // Byte mode
        const countBits = version <= 9 ? 8 : 16;
        bits += dataBytes.length.toString(2).padStart(countBits, '0');

        for (const b of dataBytes) {
            bits += b.toString(2).padStart(8, '0');
        }

        // Terminator
        const terminatorLen = Math.min(4, totalBitsReq - bits.length);
        if (terminatorLen > 0) {
            bits += '0'.repeat(terminatorLen);
        }

        // Pad to byte boundary
        if (bits.length % 8 !== 0) {
            bits += '0'.repeat(8 - (bits.length % 8));
        }

        // Pad bytes
        const padBytes = [0xec, 0x11];
        let padIdx = 0;
        let currentCws = bits.length / 8;
        while (currentCws < totalDataCw) {
            bits += padBytes[padIdx].toString(2).padStart(8, '0');
            padIdx = (padIdx + 1) % 2;
            currentCws++;
        }

        const codewords: number[] = [];
        for (let i = 0; i < bits.length; i += 8) {
            codewords.push(parseInt(bits.substring(i, i + 8), 2));
        }
        return codewords;
    }

    private polyMul(p1: number[], p2: number[]): number[] {
        const result: number[] = new Array<number>(p1.length + p2.length - 1).fill(0);
        for (let i = 0; i < p1.length; i++) {
            for (let j = 0; j < p2.length; j++) {
                result[i + j] ^= this.gfMul(p1[i], p2[j]);
            }
        }
        return result;
    }

    private getRsGenerator(degree: number): number[] {
        let poly = [1];
        for (let i = 0; i < degree; i++) {
            poly = this.polyMul(poly, [1, this.ExpTable[i]]);
        }
        return poly;
    }

    private calculateRsEcc(data: number[], gen: number[], ecCount: number): number[] {
        const remainder = [...data, ...new Array<number>(ecCount).fill(0)];
        const dataLen = data.length;

        for (let i = 0; i < dataLen; i++) {
            const coef = remainder[i];
            if (coef !== 0) {
                for (let j = 0; j < gen.length; j++) {
                    remainder[i + j] ^= this.gfMul(gen[j], coef);
                }
            }
        }
        return remainder.slice(dataLen);
    }

    private createFinalCodewords(codewords: number[], version: number): number[] {
        const info = this.getVersionInfo(version);
        const ecPerBlock = info.ecPerBlock;
        const generatorPoly = this.getRsGenerator(ecPerBlock);

        const dataBlocks: number[][] = [];
        const ecBlocks: number[][] = [];
        let offset = 0;

        for (let b = 0; b < info.b1; b++) {
            const len = info.d1;
            const blockData = codewords.slice(offset, offset + len);
            offset += len;
            dataBlocks.push(blockData);
            ecBlocks.push(this.calculateRsEcc(blockData, generatorPoly, ecPerBlock));
        }
        for (let b = 0; b < info.b2; b++) {
            const len = info.d2;
            const blockData = codewords.slice(offset, offset + len);
            offset += len;
            dataBlocks.push(blockData);
            ecBlocks.push(this.calculateRsEcc(blockData, generatorPoly, ecPerBlock));
        }

        // Interleave data
        const finalCodewords: number[] = [];
        const maxDataLen = Math.max(info.d1, info.d2);
        for (let i = 0; i < maxDataLen; i++) {
            for (const block of dataBlocks) {
                if (i < block.length) {
                    finalCodewords.push(block[i]);
                }
            }
        }

        // Interleave EC
        for (let i = 0; i < ecPerBlock; i++) {
            for (const block of ecBlocks) {
                finalCodewords.push(block[i]);
            }
        }

        return finalCodewords;
    }

    private writeFormatInfo(matrix: number[][], size: number, eclBits: number, mask: number): void {
        const formatData = (eclBits << 3) | mask;
        let bch = formatData << 10;
        const poly = 0x537;
        for (let i = 4; i >= 0; i--) {
            if ((bch & (1 << (i + 10))) !== 0) {
                bch ^= poly << i;
            }
        }
        const formatInfo = ((formatData << 10) | bch) ^ 0x5412;
        // bits[0] is b0 (LSB), bits[14] is b14 (MSB)
        const bits: number[] = [];
        for (let i = 0; i < 15; i++) {
            bits[i] = (formatInfo >> i) & 1;
        }

        // Top-left:
        // Row 8: (8,0)=b14, (8,1)=b13, (8,2)=b12, (8,3)=b11, (8,4)=b10, (8,5)=b9, (8,7)=b8, (8,8)=b7
        matrix[8][0] = bits[14];
        matrix[8][1] = bits[13];
        matrix[8][2] = bits[12];
        matrix[8][3] = bits[11];
        matrix[8][4] = bits[10];
        matrix[8][5] = bits[9];
        matrix[8][7] = bits[8];
        matrix[8][8] = bits[7];

        // Col 8: (7,8)=b6, (5,8)=b5, (4,8)=b4, (3,8)=b3, (2,8)=b2, (1,8)=b1, (0,8)=b0
        matrix[7][8] = bits[6];
        matrix[5][8] = bits[5];
        matrix[4][8] = bits[4];
        matrix[3][8] = bits[3];
        matrix[2][8] = bits[2];
        matrix[1][8] = bits[1];
        matrix[0][8] = bits[0];

        // Bottom-left: (size - 1, 8)=b0 ... (size - 7, 8)=b6
        for (let i = 0; i < 7; i++) {
            matrix[size - 1 - i][8] = bits[i];
        }

        // Top-right: (8, size - 8)=b7 ... (8, size - 1)=b14
        for (let i = 0; i < 8; i++) {
            matrix[8][size - 8 + i] = bits[7 + i];
        }
    }

    private writeVersionInfo(matrix: number[][], size: number, version: number): void {
        if (version < 7) return;
        let bch = version << 12;
        const poly = 0x1f25;
        for (let i = 5; i >= 0; i--) {
            if ((bch & (1 << (i + 12))) !== 0) {
                bch ^= poly << i;
            }
        }
        const versionBits = (version << 12) | bch;
        for (let i = 0; i < 18; i++) {
            const bit = (versionBits >> i) & 1;
            const r = Math.floor(i / 3);
            const c = i % 3;
            // Top-right: 6x3 block at rows 0..5, cols size-11..size-9
            matrix[r][size - 11 + c] = bit;
            // Bottom-left: 3x6 block at rows size-11..size-9, cols 0..5
            matrix[size - 11 + c][r] = bit;
        }
    }

    private calculatePenalty(matrix: number[][], size: number): number {
        let score = 0;

        // 1. Same-color adjacent modules (rows and columns)
        for (let r = 0; r < size; r++) {
            let count = 1;
            for (let c = 1; c < size; c++) {
                if (matrix[r][c] === matrix[r][c - 1]) {
                    count++;
                } else {
                    if (count >= 5) score += 3 + (count - 5);
                    count = 1;
                }
            }
            if (count >= 5) score += 3 + (count - 5);
        }
        for (let c = 0; c < size; c++) {
            let count = 1;
            for (let r = 1; r < size; r++) {
                if (matrix[r][c] === matrix[r - 1][c]) {
                    count++;
                } else {
                    if (count >= 5) score += 3 + (count - 5);
                    count = 1;
                }
            }
            if (count >= 5) score += 3 + (count - 5);
        }

        // 2. 2x2 blocks of same color
        for (let r = 0; r < size - 1; r++) {
            for (let c = 0; c < size - 1; c++) {
                const val = matrix[r][c];
                if (val === matrix[r + 1][c] && val === matrix[r][c + 1] && val === matrix[r + 1][c + 1]) {
                    score += 3;
                }
            }
        }

        // 3. 1:1:3:1:1 pattern
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size - 10; c++) {
                if (
                    matrix[r][c] === 1 &&
                    matrix[r][c + 1] === 0 &&
                    matrix[r][c + 2] === 1 &&
                    matrix[r][c + 3] === 1 &&
                    matrix[r][c + 4] === 1 &&
                    matrix[r][c + 5] === 0 &&
                    matrix[r][c + 6] === 1
                ) {
                    if (
                        (c >= 4 && matrix[r][c - 4] === 0 && matrix[r][c - 3] === 0 && matrix[r][c - 2] === 0 && matrix[r][c - 1] === 0) ||
                        (c + 10 < size && matrix[r][c + 7] === 0 && matrix[r][c + 8] === 0 && matrix[r][c + 9] === 0 && matrix[r][c + 10] === 0)
                    ) {
                        score += 40;
                    }
                }
            }
        }
        for (let c = 0; c < size; c++) {
            for (let r = 0; r < size - 10; r++) {
                if (
                    matrix[r][c] === 1 &&
                    matrix[r + 1][c] === 0 &&
                    matrix[r + 2][c] === 1 &&
                    matrix[r + 3][c] === 1 &&
                    matrix[r + 4][c] === 1 &&
                    matrix[r + 5][c] === 0 &&
                    matrix[r + 6][c] === 1
                ) {
                    if (
                        (r >= 4 && matrix[r - 4][c] === 0 && matrix[r - 3][c] === 0 && matrix[r - 2][c] === 0 && matrix[r - 1][c] === 0) ||
                        (r + 10 < size && matrix[r + 7][c] === 0 && matrix[r + 8][c] === 0 && matrix[r + 9][c] === 0 && matrix[r + 10][c] === 0)
                    ) {
                        score += 40;
                    }
                }
            }
        }

        // 4. Proportion of dark modules
        let darkCount = 0;
        for (let r = 0; r < size; r++) {
            for (let c = 0; c < size; c++) {
                if (matrix[r][c] === 1) darkCount++;
            }
        }
        const percent = (darkCount / (size * size)) * 100;
        const diff = Math.abs(percent - 50);
        const k = Math.floor(diff / 5);
        score += k * 10;

        return score;
    }

    private buildMatrix(codewords: number[], version: number): number[][] {
        const size = 17 + version * 4;
        const matrix: number[][] = Array.from({ length: size }, () => new Array<number>(size).fill(-1));
        const reserved: boolean[][] = Array.from({ length: size }, () => new Array<boolean>(size).fill(false));

        // 1. Finder patterns & separators
        const finders = [
            [0, 0],
            [size - 7, 0],
            [0, size - 7],
        ];
        for (const [r0, c0] of finders) {
            for (let r = 0; r < 7; r++) {
                for (let c = 0; c < 7; c++) {
                    const isBlack = r === 0 || r === 6 || c === 0 || c === 6 || (r >= 2 && r <= 4 && c >= 2 && c <= 4);
                    matrix[r0 + r][c0 + c] = isBlack ? 1 : 0;
                }
            }
            for (let r = -1; r <= 7; r++) {
                for (let c = -1; c <= 7; c++) {
                    const row = r0 + r;
                    const col = c0 + c;
                    if (row >= 0 && row < size && col >= 0 && col < size) {
                        if (r === -1 || r === 7 || c === -1 || c === 7) {
                            matrix[row][col] = 0;
                        }
                        reserved[row][col] = true;
                    }
                }
            }
        }

        // 2. Alignment patterns
        const info = this.getVersionInfo(version);
        const alignPositions = info.align;
        for (let i = 0; i < alignPositions.length; i++) {
            for (let j = 0; j < alignPositions.length; j++) {
                const r0 = alignPositions[i];
                const c0 = alignPositions[j];
                if (reserved[r0][c0]) continue;
                for (let r = -2; r <= 2; r++) {
                    for (let c = -2; c <= 2; c++) {
                        const isBlack = r === -2 || r === 2 || c === -2 || c === 2 || (r === 0 && c === 0);
                        matrix[r0 + r][c0 + c] = isBlack ? 1 : 0;
                        reserved[r0 + r][c0 + c] = true;
                    }
                }
            }
        }

        // 3. Timing patterns
        for (let i = 8; i < size - 8; i++) {
            if (!reserved[6][i]) {
                matrix[6][i] = i % 2 === 0 ? 1 : 0;
                reserved[6][i] = true;
            }
            if (!reserved[i][6]) {
                matrix[i][6] = i % 2 === 0 ? 1 : 0;
                reserved[i][6] = true;
            }
        }

        // 4. Dark module
        matrix[size - 8][8] = 1;
        reserved[size - 8][8] = true;

        // 5. Reserve format info areas
        for (let i = 0; i < 9; i++) {
            reserved[8][i] = true;
            reserved[i][8] = true;
        }
        for (let i = size - 8; i < size; i++) {
            reserved[8][i] = true;
            reserved[i][8] = true;
        }

        // 6. Reserve version info areas for version >= 7
        if (version >= 7) {
            for (let r = 0; r < 6; r++) {
                for (let c = size - 11; c < size - 8; c++) {
                    reserved[r][c] = true;
                    reserved[c][r] = true;
                }
            }
        }

        // 7. Place data codewords
        let bits = '';
        for (const cw of codewords) {
            bits += cw.toString(2).padStart(8, '0');
        }

        let bitIdx = 0;
        let dir = -1;
        let row = size - 1;
        let col = size - 1;

        while (col > 0) {
            if (col === 6) col--;
            for (let c = 0; c < 2; c++) {
                const curCol = col - c;
                if (!reserved[row][curCol]) {
                    matrix[row][curCol] = bitIdx < bits.length && bits[bitIdx] === '1' ? 1 : 0;
                    bitIdx++;
                }
            }
            row += dir;
            if (row < 0 || row >= size) {
                dir = -dir;
                row += dir;
                col -= 2;
            }
        }

        // 8. Find best mask
        let bestScore = Infinity;
        let bestMatrix: number[][] = [];

        for (let mask = 0; mask < 8; mask++) {
            const testMatrix = matrix.map((r) => [...r]);
            for (let r = 0; r < size; r++) {
                for (let c = 0; c < size; c++) {
                    if (reserved[r][c]) continue;
                    let invert = false;
                    switch (mask) {
                        case 0:
                            invert = (r + c) % 2 === 0;
                            break;
                        case 1:
                            invert = r % 2 === 0;
                            break;
                        case 2:
                            invert = c % 3 === 0;
                            break;
                        case 3:
                            invert = (r + c) % 3 === 0;
                            break;
                        case 4:
                            invert = (Math.floor(r / 2) + Math.floor(c / 3)) % 2 === 0;
                            break;
                        case 5:
                            invert = ((r * c) % 2) + ((r * c) % 3) === 0;
                            break;
                        case 6:
                            invert = (((r * c) % 2) + ((r * c) % 3)) % 2 === 0;
                            break;
                        case 7:
                            invert = (((r + c) % 2) + ((r * c) % 3)) % 2 === 0;
                            break;
                    }
                    if (invert) {
                        testMatrix[r][c] ^= 1;
                    }
                }
            }
            this.writeFormatInfo(testMatrix, size, 0, mask);
            this.writeVersionInfo(testMatrix, size, version);
            const score = this.calculatePenalty(testMatrix, size);
            if (score < bestScore) {
                bestScore = score;
                bestMatrix = testMatrix;
            }
        }

        return bestMatrix;
    }

    private generateQrMatrix(text: string): number[][] {
        this.initGf();
        const encoder = new TextEncoder();
        const dataBytes = Array.from(encoder.encode(text));
        const version = this.determineVersion(dataBytes.length);
        const codewords = this.encodeData(dataBytes, version);
        const finalBits = this.createFinalCodewords(codewords, version);
        return this.buildMatrix(finalBits, version);
    }

    public generateSvgPath(text: string, margin = 4): { path: string; viewBoxSize: number } {
        const matrix = this.generateQrMatrix(text);
        const count = matrix.length;
        const total = count + 2 * margin;

        let path = '';
        for (let r = 0; r < count; r++) {
            for (let c = 0; c < count; c++) {
                if (matrix[r][c] === 1) {
                    const x = c + margin;
                    const y = r + margin;
                    path += `M${x.toString()},${y.toString()}h1v1h-1z `;
                }
            }
        }

        return {
            path: path.trim(),
            viewBoxSize: total,
        };
    }
}

export const QrCodeService = new QrCodeServiceClass();
